import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import ResponsiveTooltip from "./ui/ResponsiveTooltip";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Check, ChevronDown, ChevronsUpDown } from "lucide-react";
import {
  CheckSquare2,
  Search,
  Clock,
  AlertTriangle,
  FileText,
  Calendar,
  User,
  Filter,
  RefreshCw,
  Shield,
  Info,
  Download,
  Loader2,
} from "lucide-react";
import { format, isAfter, isBefore } from "date-fns";
import { toast } from "sonner";
import { showToast } from "../utils/toast";
import { handleAPIError } from "../utils/errorHandler";
import { actionPointAPI, userAPI, reportAPI } from "../Services/apiService";
import Pagination from "./Pagination";
import useDebouncedValue from "../hooks/useDebouncedValue";
import { cn, truncateText } from "../lib/utils";

const TaskManagement = () => {
  const { user: currentUser } = useAuth();
  const [allTasks, setAllTasks] = useState([]); // Paginated tasks for display
  const [allTasksData, setAllTasksData] = useState([]); // Full data from backend (for dropdowns)
  const [taskStats, setTaskStats] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [filterApprover, setFilterApprover] = useState("all");
  const [assigneeOpenDesktop, setAssigneeOpenDesktop] = useState(false);
  const [assigneeOpenMobile, setAssigneeOpenMobile] = useState(false);
  const [approverOpenDesktop, setApproverOpenDesktop] = useState(false);
  const [approverOpenMobile, setApproverOpenMobile] = useState(false);
  const [allApproversFromTasks, setAllApproversFromTasks] = useState([]);
  const [allAssigneesFromTasks, setAllAssigneesFromTasks] = useState([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState("due_date"); // Default sort by due date
  const [sortDirection, setSortDirection] = useState("desc");
  const [isDownloadingSummary, setIsDownloadingSummary] = useState(false);
  const [isDownloadingActionPoints, setIsDownloadingActionPoints] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const isInitialLoad = useRef(true);

  const getAdminExportParams = () => {
    const assigneeId =
      filterAssignee !== "all" && filterAssignee
        ? parseInt(filterAssignee, 10)
        : undefined;
    const approverId =
      filterApprover !== "all" && filterApprover
        ? parseInt(filterApprover, 10)
        : undefined;
    const validAssigneeId =
      assigneeId && !isNaN(assigneeId) ? assigneeId : undefined;
    const validApproverId =
      approverId && !isNaN(approverId) ? approverId : undefined;

    return {
      tab: "all",
      search: debouncedSearch || undefined,
      status: filterStatus !== "all" ? filterStatus : undefined,
      assignee_id: validAssigneeId,
      approver_id: validApproverId,
      sort_by: sortColumn,
      sort_order: sortDirection,
    };
  };

  const getAdminExportFilename = () => {
    const tabLabel = filterStatus === "all"
      ? "All"
      : filterStatus.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join("_");
    return `Admin_Action_Points_${tabLabel}_Report_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
  };

  const handleExportActionableSummary = async () => {
    try {
      setIsDownloadingSummary(true);

      const response = await reportAPI.exportActionableSummary();
      const blob = new Blob(
        [response.data],
        { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Actionable_Summary_Report_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast.success("Actionable summary exported successfully");
    } catch (error) {
      handleAPIError(error, "Failed to export actionable summary");
    } finally {
      setIsDownloadingSummary(false);
    }
  };

  const handleExportActionPoints = async () => {
    try {
      setIsDownloadingActionPoints(true);

      const response = await actionPointAPI.exportAllActionPointsAdmin(getAdminExportParams());
      const blob = new Blob(
        [response.data],
        { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", getAdminExportFilename());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast.success("Action points exported successfully");
    } catch (error) {
      handleAPIError(error, "Failed to export action points", true);
    } finally {
      setIsDownloadingActionPoints(false);
    }
  };

  // Fetch all approvers and assignees from tasks (for filter dropdown)
  useEffect(() => {
    const fetchAllApproversAndAssignees = async () => {
      if (!currentUser?.token) return;

      try {
        // Fetch all tasks with a large page size to get all approvers and assignees
        const response = await actionPointAPI.getAllActionPointsAdmin({
          tab: "all",
          page: 1,
          page_size: 1000, // Large page size to get all tasks for extraction
        });

        const tasks = response.data?.items || [];
        // Extract unique approvers and assignees from tasks
        const approverMap = new Map();
        const assigneeMap = new Map();

        tasks.forEach((task) => {
          // Extract approvers
          if (task.approver && task.approver.id) {
            approverMap.set(task.approver.id, {
              id: task.approver.id,
              name: task.approver.full_name || task.approver.name || "Unknown",
            });
          }

          // Extract assignees (handle both single assignee and multiple assignees)
          if (
            task.assignees &&
            Array.isArray(task.assignees) &&
            task.assignees.length > 0
          ) {
            task.assignees.forEach((assignee) => {
              if (assignee && assignee.id) {
                assigneeMap.set(assignee.id, {
                  id: assignee.id,
                  name: assignee.full_name || assignee.name || "Unknown",
                });
              }
            });
          } else if (task.assignee && task.assignee.id) {
            assigneeMap.set(task.assignee.id, {
              id: task.assignee.id,
              name: task.assignee.full_name || task.assignee.name || "Unknown",
            });
          }
        });

        setAllApproversFromTasks(Array.from(approverMap.values()));
        setAllAssigneesFromTasks(Array.from(assigneeMap.values()));
      } catch (error) {
        // Silently fail - we'll still have role-based approvers and assignees
        console.error(
          "Failed to fetch approvers and assignees from tasks:",
          error
        );
      }
    };

    fetchAllApproversAndAssignees();
  }, [currentUser?.token]);

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setPage(1);
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return (
        <span className="text-gray-600 text-xs ml-1">
          {sortDirection === "asc" ? "▲" : "▼"}
        </span>
      );
    }
    return <span className="text-gray-400 text-xs ml-1">▼</span>;
  };

  const loadTasks = useCallback(async () => {
    if (!currentUser?.token) return;

    try {
      if (isInitialLoad.current) {
        setLoading(true);
        isInitialLoad.current = false;
      }

      const assigneeId =
        filterAssignee !== "all" && filterAssignee
          ? parseInt(filterAssignee, 10)
          : undefined;
      const approverId =
        filterApprover !== "all" && filterApprover
          ? parseInt(filterApprover, 10)
          : undefined;

      const validAssigneeId =
        assigneeId && !isNaN(assigneeId) ? assigneeId : undefined;
      const validApproverId =
        approverId && !isNaN(approverId) ? approverId : undefined;

      const tasksResponse = await actionPointAPI.getAllActionPointsAdmin({
        tab: "all",
        search: debouncedSearch || undefined,
        status: filterStatus !== "all" ? filterStatus : undefined,
        assignee_id: validAssigneeId,
        approver_id: validApproverId,
        sort_by: sortColumn,
        sort_order: sortDirection,
        page,
        page_size: pageSize,
      });

      const [usersResponse, statsResponse] = await Promise.all([
        userAPI.getUsers({ limit: 200 }),
        actionPointAPI.getAllActionPointsAdmin({
          tab: "all",
          page: 1,
          page_size: 1,
        }),
      ]);

      const responseData = tasksResponse.data;
      const users = Array.isArray(usersResponse.data)
        ? usersResponse.data
        : usersResponse.data?.items || [];

      let paginatedItems, total, pages;

      if (responseData?.items && responseData?.total !== undefined) {
        paginatedItems = responseData.items || [];
        total = responseData.total || 0;
        pages = responseData.pages || 1;

        setAllTasks(paginatedItems);
        setTotal(total);
        setPages(pages);
        setHasNext(responseData.has_next || false);
        setHasPrev(responseData.has_prev || false);
        setTaskStats(
          statsResponse?.data?.counts || responseData.counts || {}
        );
      } else {
        const allTasksFromAPI = responseData?.items || [];
        setAllTasksData(allTasksFromAPI);

        total = allTasksFromAPI.length;
        pages = Math.max(1, Math.ceil(total / pageSize));
        const currentPage = Math.min(Math.max(1, page), pages);
        const startIdx = (currentPage - 1) * pageSize;
        const endIdx = startIdx + pageSize;
        paginatedItems = allTasksFromAPI.slice(startIdx, endIdx);

        setAllTasks(paginatedItems);
        setTotal(total);
        setPages(pages);
        setHasNext(currentPage < pages);
        setHasPrev(currentPage > 1);
        setTaskStats(responseData?.counts || {});
      }

      setAllUsers(users);
    } catch (error) {
      handleAPIError(error, "Failed to load action points", true);
      setAllTasks([]);
      setTaskStats(null);
    } finally {
      setLoading(false);
    }
  }, [
    currentUser?.token,
    page,
    pageSize,
    debouncedSearch,
    filterStatus,
    filterAssignee,
    filterApprover,
    sortColumn,
    sortDirection,
  ]);

  // Keep a ref to the latest loadTasks so the interval never reads a stale closure
  const loadTasksRef = useRef(loadTasks);
  useEffect(() => {
    loadTasksRef.current = loadTasks;
  });

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Auto-refresh every 30s — created once so it never stacks on filter changes
  useEffect(() => {
    const refreshInterval = setInterval(() => loadTasksRef.current(), 30000);
    return () => clearInterval(refreshInterval);
  }, []);

  // Handle manual refresh
  const handleRefreshData = async () => {
    try {
      setLoading(true);
      // Parse filter IDs safely - ensure they're valid integers
      const assigneeId =
        filterAssignee !== "all" && filterAssignee
          ? parseInt(filterAssignee, 10)
          : undefined;
      const approverId =
        filterApprover !== "all" && filterApprover
          ? parseInt(filterApprover, 10)
          : undefined;

      // Validate parsed IDs are not NaN
      const validAssigneeId =
        assigneeId && !isNaN(assigneeId) ? assigneeId : undefined;
      const validApproverId =
        approverId && !isNaN(approverId) ? approverId : undefined;

      // OPTIMIZED: Execute sequentially to reduce concurrent requests
      // First fetch tasks (critical), then users (for dropdowns), then stats (non-critical)
      const tasksResponse = await actionPointAPI.getAllActionPointsAdmin({
        tab: "all",
        search: debouncedSearch || undefined,
        status: filterStatus !== "all" ? filterStatus : undefined,
        assignee_id: validAssigneeId, // Backend filter - supports multiple assignees
        approver_id: validApproverId, // Backend filter
        sort_by: sortColumn,
        sort_order: sortDirection,
        page: page,
        page_size: pageSize,
      });

      // Fetch users and stats in parallel (both non-critical for display)
      const [usersResponse, statsResponse] = await Promise.all([
        userAPI.getUsers({ limit: 200 }), // Get all users for dropdowns (max 200)
        // Fetch stats separately without any filters to show total counts
        actionPointAPI.getAllActionPointsAdmin({
          tab: "all",
          page: 1,
          page_size: 1, // Just need counts, not data
        }),
      ]);

      const responseData = tasksResponse.data;
      // Handle both paginated and non-paginated user response
      const users = Array.isArray(usersResponse.data)
        ? usersResponse.data
        : usersResponse.data?.items || [];

      if (responseData?.items && responseData?.total !== undefined) {
        // Backend returned paginated response
        setAllTasks(responseData.items || []);
        setTotal(responseData.total || 0);
        setPages(responseData.pages || 1);
        setHasNext(responseData.has_next || false);
        setHasPrev(responseData.has_prev || false);
        // Use stats from separate call (total counts, not filtered)
        setTaskStats(statsResponse?.data?.counts || responseData.counts || {});
      } else {
        // Backward compatibility
        const tasks = responseData?.items || [];
        setAllTasks(tasks);
        setTaskStats(responseData?.counts || {});
      }

      setAllUsers(users);
      showToast.success("Tasks refreshed successfully");
    } catch (error) {
      handleAPIError(error, "Failed to refresh tasks", true);
    } finally {
      setLoading(false);
    }
  };

  // Backend handles filtering, sorting, and we do client-side pagination
  // allTasks already contains paginated data from backend
  const paginatedTasks = allTasks;

  // Check if task is overdue
  const isTaskOverdue = (task) => {
    if (!task.due_date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(task.due_date);
    dueDate.setHours(0, 0, 0, 0);
    // Task is overdue if due date is in the past AND status allows it to be overdue
    // Only pending or rejected tasks can be overdue
    if (
      dueDate < today &&
      (task.status === "pending" || task.status === "rejected_in_review")
    ) {
      return true;
    }
    return false;
  };

  const getStatusBadge = (task) => {
    // Check if task is overdue first - show overdue status even if status is pending
    if (isTaskOverdue(task)) {
      return (
        <Badge variant="destructive" className="bg-red-600 text-white">
          Overdue
        </Badge>
      );
    }

    const status = task.status;
    switch (status) {
      case "completed":
        return (
          <Badge variant="outline" className="text-green-600">
            Completed
          </Badge>
        );
      case "approved":
        return (
          <Badge variant="outline" className="text-green-600">
            Completed
          </Badge>
        ); // Backward compatibility
      case "pending":
        return (
          <Badge variant="outline" className="text-white bg-[#FFC107]">
            Pending
          </Badge>
        );
      case "submitted_for_review":
        return (
          <Badge variant="outline" className="text-purple-600">
            Submitted for Review
          </Badge>
        );
      case "rejected_in_review":
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        // Convert status to proper title case (e.g., "submitted_for_review" -> "Submitted For Review")
        const formattedStatus = status
          ? status
            .replace(/_/g, " ")
            .split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" ")
          : status;
        return <Badge variant="secondary">{formattedStatus}</Badge>;
    }
  };

  // Calculate days until due date
  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get due date color based on urgency - matching color guide
  const getDueDateColor = (dueDate, status) => {
    // If already completed, show green
    if (status === "completed") {
      return "text-green-600";
    }

    const daysUntil = getDaysUntilDue(dueDate);
    if (daysUntil === null) return "text-gray-600";

    if (daysUntil < 0) {
      return "text-red-700 font-semibold"; // Overdue - red-700 to match guide
    } else if (daysUntil === 0) {
      return "text-orange-600 font-semibold"; // Due today - orange-600 to match guide
    } else if (daysUntil === 1) {
      return "text-sky-500 font-medium"; // Due tomorrow - sky-500 to match guide
    } else if (daysUntil <= 3) {
      return "text-yellow-500"; // Due within 3 days - yellow-500 to match guide
    } else if (daysUntil <= 7) {
      return "text-violet-500"; // Due within a week - violet-500 to match guide
    } else {
      return "text-gray-500"; // More than a week - gray-500 to match guide
    }
  };

  // Get unique assignees for filter (combine role-based and task-based assignees)
  const assignees = useMemo(() => {
    // Start with role-based assignees
    const roleBasedAssignees = allUsers
      .filter(
        (u) => u.role && ["employee", "business_head", "admin"].includes(u.role)
      )
      .map((u) => ({ id: u.id, name: u.full_name }));

    // Combine with assignees from actual tasks
    const assigneeMap = new Map();

    // Add role-based assignees
    roleBasedAssignees.forEach((assignee) => {
      assigneeMap.set(assignee.id, assignee);
    });

    // Add assignees from tasks (this ensures all assignees in meetings are included)
    allAssigneesFromTasks.forEach((assignee) => {
      if (!assigneeMap.has(assignee.id)) {
        assigneeMap.set(assignee.id, assignee);
      }
    });

    // Also extract from current tasks (in case new tasks were added)
    allTasks.forEach((task) => {
      // Handle multiple assignees
      if (
        task.assignees &&
        Array.isArray(task.assignees) &&
        task.assignees.length > 0
      ) {
        task.assignees.forEach((assignee) => {
          if (assignee && assignee.id) {
            const assigneeId = assignee.id;
            if (!assigneeMap.has(assigneeId)) {
              assigneeMap.set(assigneeId, {
                id: assigneeId,
                name: assignee.full_name || assignee.name || "Unknown",
              });
            }
          }
        });
      } else if (task.assignee && task.assignee.id) {
        // Handle single assignee (backward compatibility)
        const assigneeId = task.assignee.id;
        if (!assigneeMap.has(assigneeId)) {
          assigneeMap.set(assigneeId, {
            id: assigneeId,
            name: task.assignee.full_name || task.assignee.name || "Unknown",
          });
        }
      }
    });

    // Convert to array and sort
    return Array.from(assigneeMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [allUsers, allAssigneesFromTasks, allTasks]);

  // Get unique approvers for filter (combine role-based and task-based approvers)
  const approvers = useMemo(() => {
    // Start with role-based approvers
    const roleBasedApprovers = allUsers
      .filter((u) => u.role && ["business_head", "admin"].includes(u.role))
      .map((u) => ({ id: u.id, name: u.full_name }));

    // Combine with approvers from actual tasks
    const approverMap = new Map();

    // Add role-based approvers
    roleBasedApprovers.forEach((approver) => {
      approverMap.set(approver.id, approver);
    });

    // Add approvers from tasks (this ensures all approvers in meetings are included)
    allApproversFromTasks.forEach((approver) => {
      if (!approverMap.has(approver.id)) {
        approverMap.set(approver.id, approver);
      }
    });

    // Also extract from current tasks (in case new tasks were added)
    allTasks.forEach((task) => {
      if (task.approver && task.approver.id) {
        const approverId = task.approver.id;
        if (!approverMap.has(approverId)) {
          approverMap.set(approverId, {
            id: approverId,
            name: task.approver.full_name || task.approver.name || "Unknown",
          });
        }
      }
    });

    // Convert to array and sort
    return Array.from(approverMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [allUsers, allApproversFromTasks, allTasks]);

  if (loading) {
    return (
      <Card className="glass-card border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare2 className="h-5 w-5 text-blue-600" />
            Task Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="loading-shimmer h-32 rounded-lg"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card  border-0 p-2">
      <div className="hidden xl:flex flex-col gap-2 py-3 px-3 bg-gray-50 border border-gray-100 rounded-lg w-full mb-2">
        {/* Filters, stats, and exports — single row */}
        <div className="w-full min-w-0 overflow-x-auto overflow-y-visible py-0.5 -my-0.5">
          <div className="flex items-center gap-2.5 min-w-max">
            {/* Status Badges */}
            {taskStats && (
              <div className="flex items-center gap-2 border-r border-gray-300 pr-2.5 shrink-0">
                <div className="flex items-center gap-1.5 text-sm font-medium text-yellow-800 bg-yellow-100 px-2.5 py-1.5 rounded-full border border-yellow-200 shadow-sm whitespace-nowrap">
                  <Clock className="w-3.5 h-3.5 text-yellow-600" /> Pending: <span className="font-bold">{taskStats.pending}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm font-medium text-green-800 bg-green-100 px-2.5 py-1.5 rounded-full border border-green-200 shadow-sm whitespace-nowrap">
                  <CheckSquare2 className="w-3.5 h-3.5 text-green-600" /> Completed: <span className="font-bold">{taskStats.completed}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm font-medium text-red-800 bg-red-100 px-2.5 py-1.5 rounded-full border border-red-200 shadow-sm whitespace-nowrap">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> Overdue: <span className="font-bold">{taskStats.overdue}</span>
                </div>
              </div>
            )}

            {/* Filters */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32 focus-gold bg-white h-9 shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="submitted_for_review">Review</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected_in_review">Rejected</SelectItem>
              </SelectContent>
            </Select>

            <Popover open={assigneeOpenDesktop} onOpenChange={setAssigneeOpenDesktop}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={assigneeOpenDesktop} className="flex focus-gold font-normal justify-between bg-white w-32 h-9 px-3 shrink-0">
                  <span className="truncate">
                    {filterAssignee === "all" ? "All Assignees" : (assignees.find((a) => a.id.toString() === filterAssignee)?.name || "All Assignees")}
                  </span>
                  <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0" align="start">
                <Command shouldFilter={true}>
                  <CommandInput placeholder="Search assignees..." className="h-9" />
                  <CommandList className="max-h-[200px]">
                    <CommandEmpty>No assignee found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem value="all assignees" onSelect={() => { setFilterAssignee("all"); setPage(1); setAssigneeOpenDesktop(false); }}>
                        All Assignees
                      </CommandItem>
                      {assignees.map((assignee) => (
                        <CommandItem key={assignee.id} value={`${assignee.name} ${assignee.id} assignee`} onSelect={() => { setFilterAssignee(assignee.id.toString()); setPage(1); setAssigneeOpenDesktop(false); }}>
                          {assignee.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            <Popover open={approverOpenDesktop} onOpenChange={setApproverOpenDesktop}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={approverOpenDesktop} className="flex focus-gold font-normal justify-between bg-white w-32 h-9 px-3 shrink-0">
                  <span className="truncate">
                    {filterApprover === "all" ? "All Approvers" : (approvers.find((a) => a.id.toString() === filterApprover)?.name || "All Approvers")}
                  </span>
                  <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0" align="start">
                <Command shouldFilter={true}>
                  <CommandInput placeholder="Search approvers..." className="h-9" />
                  <CommandList className="max-h-[200px]">
                    <CommandEmpty>No approver found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem value="all approvers" onSelect={() => { setFilterApprover("all"); setPage(1); setApproverOpenDesktop(false); }}>
                        All Approvers
                      </CommandItem>
                      {approvers.map((approver) => (
                        <CommandItem key={approver.id} value={`${approver.name} ${approver.id} approver`} onSelect={() => { setFilterApprover(approver.id.toString()); setPage(1); setApproverOpenDesktop(false); }}>
                          {approver.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Search Input */}
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 focus-gold h-9 w-36 bg-white"
              />
            </div>

            {/* Export Buttons */}
            <Button
              variant="outline"
              size="sm"
              className="focus-gold text-green-700 border-green-200 hover:bg-green-50 bg-white h-9 px-2.5 shrink-0 whitespace-nowrap text-xs"
              onClick={handleExportActionPoints}
              disabled={isDownloadingActionPoints}
            >
              {isDownloadingActionPoints ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Action Points Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="focus-gold text-blue-600 border-blue-200 hover:bg-blue-50 bg-white h-9 px-2.5 shrink-0 whitespace-nowrap text-xs"
              onClick={handleExportActionableSummary}
              disabled={isDownloadingSummary}
            >
              {isDownloadingSummary ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Actionable Summary Export
            </Button>
          </div>
        </div>

        {/* Due Date Color Legend */}
        <div className="flex items-center gap-3 text-[11px] font-medium bg-blue-50/50 border border-blue-100 rounded-lg px-4 py-2 shrink-0">
          <span className="font-semibold text-blue-900 mr-1">Due Date:</span>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-red-700 rounded-sm"></div><span className="text-gray-700">Overdue</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-orange-600 rounded-sm"></div><span className="text-gray-700">Today</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-sky-500 rounded-sm"></div><span className="text-gray-700">Tomorrow</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-yellow-500 rounded-sm"></div><span className="text-gray-700">2-3 days</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-violet-500 rounded-sm"></div><span className="text-gray-700">4-7 days</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-gray-500 rounded-sm"></div><span className="text-gray-700">8+ days</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-green-600 rounded-sm"></div><span className="text-gray-700">Completed</span></div>
        </div>
      </div>

      <div className="sm:hidden flex flex-col gap-4 justify-center gap-5 p-4">
        <div>
          {taskStats && (
            <div className="grid grid-cols-2  gap-4">
              {/* <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <div className='flex gap-2 items-center'>
                <p className="text-sm font-medium text-nowrap text-blue-600">Total Tasks</p>
                <CheckSquare2 className="w-4 h-4 text-blue-600" />
                </div>
              
                  <p className="text-2xl font-bold text-blue-900">{taskStats.total}</p>
              </div>
             
            </div>
          </div> */}

              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex gap-2 items-center">
                      <p className="text-sm font-medium text-yellow-600">
                        Pending
                      </p>
                      <Clock className="w-4 h-4 text-yellow-600" />
                    </div>
                    <p className="text-2xl font-bold text-yellow-900">
                      {taskStats.pending}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex gap-2 items-center">
                      <p className="text-sm font-medium text-green-600">
                        Completed
                      </p>
                      <CheckSquare2 className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-green-900">
                      {taskStats.completed}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex gap-2 items-center">
                      <p className="text-sm font-medium text-red-600">
                        Overdue
                      </p>
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </div>
                    <p className="text-2xl font-bold text-red-900">
                      {taskStats.overdue}
                    </p>
                  </div>
                </div>
              </div>
              <div className="  ">
                <div className="flex  flex-col gap-2">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full focus-gold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="w-[90%]">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                      <SelectItem value="submitted_for_review">
                        Submitted for Review
                      </SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="rejected_in_review">
                        Rejected in Review
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  <Popover
                    open={assigneeOpenMobile}
                    onOpenChange={setAssigneeOpenMobile}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={assigneeOpenMobile}
                        className="w-full px-2 focus-gold justify-between"
                      >
                        <div className="flex items-center justify-between w-full">
                          {filterAssignee === "all" ? (
                            <span className="truncate max-w-[65vw]">
                              All Assignees
                            </span>
                          ) : (
                            (() => {
                              const name =
                                assignees.find(
                                  (a) => a.id.toString() === filterAssignee
                                )?.name || "All Assignees";
                              return (
                                <ResponsiveTooltip
                                  content={
                                    <p className="text-sm whitespace-pre-wrap">
                                      {name}
                                    </p>
                                  }
                                >
                                  <span className="truncate max-w-[65vw]">
                                    {name}
                                  </span>
                                </ResponsiveTooltip>
                              );
                            })()
                          )}
                          <ChevronsUpDown className=" h-2 w-2  opacity-50" />
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-0" align="start">
                      <Command shouldFilter={true}>
                        <CommandInput
                          placeholder="Search assignees..."
                          className="h-9"
                        />
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty>No assignee found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="all assignees"
                              onSelect={() => {
                                setFilterAssignee("all");
                                setPage(1);
                                setAssigneeOpenMobile(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  filterAssignee === "all"
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                              All Assignees
                            </CommandItem>
                            {assignees.map((assignee) => (
                              <CommandItem
                                key={assignee.id}
                                value={`${assignee.name} ${assignee.id} assignee`}
                                onSelect={() => {
                                  setFilterAssignee(assignee.id.toString());
                                  setPage(1);
                                  setAssigneeOpenMobile(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    filterAssignee === assignee.id.toString()
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                                {assignee.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          )}
        </div>
        <div>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className=" flex flex-col gap-3">
              <div className="flex  gap-2">
                <Popover
                  open={approverOpenMobile}
                  onOpenChange={setApproverOpenMobile}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={approverOpenMobile}
                      className="w-40 focus-gold justify-between"
                    >
                      <div className="flex items-center justify-between w-full">
                        {filterApprover === "all" ? (
                          <span className="truncate max-w-[65vw]">
                            All Approvers
                          </span>
                        ) : (
                          (() => {
                            const name =
                              approvers.find(
                                (a) => a.id.toString() === filterApprover
                              )?.name || "All Approvers";
                            return (
                              <ResponsiveTooltip
                                content={
                                  <p className="text-sm whitespace-pre-wrap">
                                    {name}
                                  </p>
                                }
                              >
                                <span className="truncate max-w-[65vw]">
                                  {name}
                                </span>
                              </ResponsiveTooltip>
                            );
                          })()
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-0" align="start">
                    <Command shouldFilter={true}>
                      <CommandInput
                        placeholder="Search approvers..."
                        className="h-9"
                      />
                      <CommandList className="max-h-[200px]">
                        <CommandEmpty>No approver found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all approvers"
                            onSelect={() => {
                              setFilterApprover("all");
                              setPage(1);
                              setApproverOpenMobile(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                filterApprover === "all"
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            All Approvers
                          </CommandItem>
                          {approvers.map((approver) => (
                            <CommandItem
                              key={approver.id}
                              value={`${approver.name} ${approver.id} approver`}
                              onSelect={() => {
                                setFilterApprover(approver.id.toString());
                                setPage(1);
                                setApproverOpenMobile(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  filterApprover === approver.id.toString()
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                              {approver.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                <div className="flex gap-2 !w-[100%]">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                    <Input
                      placeholder="Search "
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 focus-gold w-full"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="focus-gold text-green-700 border-green-200 hover:bg-green-50 px-3"
                    onClick={handleExportActionPoints}
                    disabled={isDownloadingActionPoints}
                    title="Action Points Export"
                  >
                    {isDownloadingActionPoints ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    className="focus-gold text-blue-600 border-blue-200 hover:bg-blue-50 px-3"
                    onClick={handleExportActionableSummary}
                    disabled={isDownloadingSummary}
                    title="Actionable Summary Export"
                  >
                    {isDownloadingSummary ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="bg-blue-50 border border-blue-200 w-full rounded-lg p-1">
            <div className="flex  gap-3">
              {/* <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" /> */}
              <div className="">
                <h4 className="font-semibold text-blue-900 ">
                  Due Date Color Guide
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-3 gap-1 space-x-2 text-xs">
                  <div className="flex items-center ml-2 gap-1.5 ">
                    <div className="w-3 h-3 bg-red-700 rounded"></div>
                    <span className="text-red-700 font-semibold">Overdue</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-orange-600 rounded"></div>
                    <span className="text-orange-600 font-semibold">Today</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-sky-500 rounded"></div>
                    <span className="text-sky-500 font-medium">Tomorrow</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                    <span className="text-yellow-500">2-3 days</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-violet-500 rounded"></div>
                    <span className="text-violet-500">4-7 days</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-gray-500 rounded"></div>
                    <span className="text-gray-500">8+ days</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-green-600 rounded"></div>
                    <span className="text-green-600">Completed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CardContent className="space-y-6 mt-2">
        {/* Stats Cards */}

        {/* Color Legend Info Card */}

        {/* Tasks Display */}
        {paginatedTasks.length === 0 ? (
          <div className="text-center py-8">
            <CheckSquare2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No tasks found</p>
            <p className="text-sm text-gray-400 mt-1">
              Try adjusting your search criteria
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block rounded-lg border overflow-hidden">
              <div className="max-h-[calc(100vh-290px)] overflow-auto">
                <div className="relative w-full overflow-visible">
                  <table className="w-full caption-bottom text-sm">
                    <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                      <tr className="bg-gray-100 border-b">
                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                          Sr. <br />
                          No.
                        </th>
                        <th
                          className="h-10 px-2 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort("meeting")}
                        >
                          <div className="flex items-center">
                            Task
                            {getSortIcon("meeting")}
                          </div>
                        </th>
                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                          <div className="flex items-center gap-2">
                            Assignee
                            <User className="w-4 h-4 text-gray-400" />
                          </div>
                        </th>
                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                          <div className="flex items-center gap-2">
                            Approver
                            <Shield className="w-4 h-4 text-gray-400" />
                          </div>
                        </th>
                        <th
                          className="h-10 px-2 text-left align-middle font-medium text-muted-foreground cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort("status")}
                        >
                          <div className="flex justify-center items-center ">
                            Status
                            {getSortIcon("status")}
                          </div>
                        </th>
                        <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">
                          <div className="flex items-center justify-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            Assigned Date
                          </div>
                        </th>
                        <th
                          className="h-10 px-2 text-center align-middle font-medium text-muted-foreground cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort("due_date")}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <div>
                              <Calendar className="w-4 h-4 text-gray-400" />
                            </div>
                            <div className="flex items-center gap-2">
                              Due Date
                              {getSortIcon("due_date")}
                            </div>
                          </div>
                        </th>
                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                          <div className="flex items-center  gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            Completed Date
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="[&_tr:last-child]:border-0">
                      {paginatedTasks.map((task, index) => (
                        <tr
                          key={task.id}
                          className="border-b transition-colors hover:bg-gray-50"
                        >
                          <td className="p-2 align-middle">
                            {(page - 1) * pageSize + index + 1}
                          </td>
                          <td className="p-2 align-middle">
                            <div className="space-y-1">
                              {task.description &&
                                task.description.length > 20 ? (
                                <ResponsiveTooltip
                                  content={
                                    <p className="max-w-xs whitespace-pre-wrap">
                                      {task.description}
                                    </p>
                                  }
                                >
                                  <p className="font-medium text-gray-900 cursor-pointer">
                                    {truncateText(task.description, 20)}
                                  </p>
                                </ResponsiveTooltip>
                              ) : (
                                <p className="font-medium text-gray-900">
                                  {task.description}
                                </p>
                              )}
                              {task.meeting?.title &&
                                task.meeting.title.length > 20 ? (
                                <ResponsiveTooltip
                                  content={
                                    <p className="max-w-xs whitespace-pre-wrap">
                                      {task.meeting.title}
                                    </p>
                                  }
                                >
                                  <span className="text-sm text-gray-600 cursor-pointer">
                                    {truncateText(task.meeting?.title, 20)}
                                  </span>
                                </ResponsiveTooltip>
                              ) : (
                                <span className="text-sm text-gray-600">
                                  {task.meeting?.title || "-"}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-2 align-middle">
                            <div className="flex items-center gap-2">
                              <ResponsiveTooltip
                                content={
                                  <div className="space-y-1">
                                    <div className="text-xs font-semibold text-gray-700 mb-1">
                                      Assignees (
                                      {task.assignees?.length ||
                                        (task.assignee ? 1 : 0)}
                                      ):
                                    </div>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {task.assignees &&
                                        task.assignees.length > 0 ? (
                                        task.assignees.map((assignee, idx) => (
                                          <div
                                            key={assignee.id || idx}
                                            className="text-xs border-b border-gray-200 text-gray-600 py-0.5 px-1 rounded hover:bg-gray-100"
                                          >
                                            {assignee.full_name ||
                                              assignee.email ||
                                              "Unknown"}
                                          </div>
                                        ))
                                      ) : task.assignee ? (
                                        <div className="text-xs border-b border-gray-200 text-gray-600 py-0.5 px-1 rounded hover:bg-gray-100">
                                          {task.assignee.full_name ||
                                            task.assignee.email ||
                                            "Unknown"}
                                        </div>
                                      ) : (
                                        <div className="text-xs text-gray-600 py-0.5 px-1">
                                          No assignees
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                }
                                side="top"
                                className="max-w-xs p-2 bg-white border border-gray-200"
                              >
                                <span className="text-sm cursor-pointer">
                                  {task.assignees && task.assignees.length > 0
                                    ? truncateText(
                                      task.assignees
                                        .map((a) => a.full_name)
                                        .join(", "),
                                      15
                                    )
                                    : truncateText(
                                      task.assignee?.full_name ||
                                      "Unassigned",
                                      15
                                    )}
                                </span>
                              </ResponsiveTooltip>
                            </div>
                          </td>
                          <td className="p-2 align-middle">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">
                                {task.approver?.full_name || "Not Assigned"}
                              </span>
                            </div>
                          </td>
                          <td className="p-2 text-center align-middle">
                            {getStatusBadge(task)}
                          </td>
                          <td className="p-2 align-middle">
                            <div className="text-sm text-center text-gray-600">
                              {task.created_at
                                ? format(
                                  new Date(task.created_at),
                                  "MMM dd, yyyy"
                                )
                                : "-"}
                            </div>
                          </td>
                          <td className="p-2 align-middle">
                            <div className="flex items-center justify-center gap-2">
                              <div
                                className={`text-sm ${getDueDateColor(
                                  task.due_date,
                                  task.status
                                )}`}
                              >
                                {task.due_date
                                  ? format(
                                    new Date(task.due_date),
                                    "MMM dd, yyyy"
                                  )
                                  : "No due date"}
                              </div>
                            </div>
                          </td>
                          <td className="p-2 align-middle">
                            <div className="text-sm text-center text-gray-600">
                              {(task.status === "completed" ||
                                task.status === "approved") &&
                                task.approved_at
                                ? format(
                                  new Date(task.approved_at),
                                  "MMM dd, yyyy"
                                )
                                : "-"}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Pagination Controls - Desktop */}
            <div className="hidden md:block">
              <Pagination
                page={page}
                pages={pages}
                total={total}
                pageSize={pageSize}
                hasNext={hasNext}
                hasPrev={hasPrev}
                onPageChange={(p) => setPage(Math.max(1, p))}
                onPageSizeChange={(s) => {
                  setPageSize(Number(s));
                  setPage(1);
                }}
              />
            </div>

            {/* Mobile Accordion View */}
            <div className="md:hidden ">
              <Accordion
                type="single"
                collapsible
                className="w-full  space-y-1"
              >
                {paginatedTasks.map((task) => (
                  <AccordionItem
                    key={task.id}
                    value={`task-${task.id}`}
                    className="border w-full rounded-lg px-4"
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-start justify-between w-full pr-4">
                        <div className="flex-1 text-left space-y-1">
                          {task.description && task.description.length > 20 ? (
                            <ResponsiveTooltip
                              content={
                                <p className="max-w-xs whitespace-pre-wrap">
                                  {task.description}
                                </p>
                              }
                            >
                              <p className="font-medium text-gray-900 text-sm cursor-pointer">
                                {truncateText(task.description, 20)}
                              </p>
                            </ResponsiveTooltip>
                          ) : (
                            <p className="font-medium text-gray-900 text-sm">
                              {task.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 flex-wrap">
                            {getStatusBadge(task)}
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2 pb-4">
                      <div className="space-y-3">
                        {task.meeting?.title && (
                          <div className="flex items-start gap-2 text-sm">
                            <FileText className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                            <div>
                              <span className="text-gray-500">Meeting: </span>
                              {task.meeting.title && task.meeting.title.length > 20 ? (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button className="text-gray-900 hover:underline cursor-pointer">
                                      {truncateText(task.meeting.title, 20)}
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-72 p-3" align="start">
                                    <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                      {task.meeting.title}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              ) : (
                                <span className="text-gray-900">
                                  {task.meeting.title}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-gray-400 shrink-0" />
                          <div>
                            <span className="text-gray-500">
                              Assignee
                              {task.assignees && task.assignees.length > 1
                                ? "s"
                                : ""}
                              :{" "}
                            </span>
                            <span className="text-gray-900">
                              {(() => {
                                const names = task.assignees && task.assignees.length > 0
                                  ? task.assignees.map((a) => a.full_name).join(", ")
                                  : task.assignee?.full_name || "Unassigned";

                                return names.length > 20 ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button className="hover:underline cursor-pointer">
                                        {truncateText(names, 20)}
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-72 p-3" align="start">
                                      <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                        {names}
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  names
                                );
                              })()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Shield className="w-4 h-4 text-gray-400 shrink-0" />
                          <div>
                            <span className="text-gray-500">Approver: </span>
                            <span className="text-gray-900">
                              {task.approver?.full_name || "Not Assigned"}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                          <div>
                            <span className="text-gray-500">
                              Assigned Date:{" "}
                            </span>
                            <span className="text-gray-900">
                              {task.created_at
                                ? format(
                                  new Date(task.created_at),
                                  "MMM dd, yyyy"
                                )
                                : "-"}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                          <div>
                            <span className="text-gray-500">Due Date: </span>
                            <span
                              className={getDueDateColor(
                                task.due_date,
                                task.status
                              )}
                            >
                              {task.due_date
                                ? format(
                                  new Date(task.due_date),
                                  "MMM dd, yyyy"
                                )
                                : "No due date"}
                            </span>
                          </div>
                        </div>
                        {(task.status === "completed" ||
                          task.status === "approved") &&
                          task.approved_at && (
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="w-4 h-4 text-green-400 shrink-0" />
                              <div>
                                <span className="text-gray-500">
                                  Completed Date:{" "}
                                </span>
                                <span className="text-green-600 font-medium">
                                  {format(
                                    new Date(task.approved_at),
                                    "MMM dd, yyyy"
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </>
        )}

        {/* Pagination Controls - Mobile */}
        <div className="md:hidden">
          <Pagination
            page={page}
            pages={pages}
            total={total}
            pageSize={pageSize}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onPageChange={(p) => setPage(Math.max(1, p))}
            onPageSizeChange={(s) => {
              setPageSize(Number(s));
              setPage(1);
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default TaskManagement;
